import { PublicIdSchemas } from '@idc-forms/id'
import { z } from 'zod'
import { AVAILABILITY_STATUS, STRUCTURE_TYPE, UNIT_TYPE } from '../entities/property/property'
import { NonNegativeMoneySchema } from '../primitives/money'
import { requiredString } from '../primitives/string'
import { InstantSchema } from '../primitives/time'
import { V2CursorPaginationSchema } from './http'

/**
 * Contrato de la administración v2 de inmuebles (property). Séptimo catálogo del
 * backoffice en v2 y el PRIMERO con DOS relaciones: un inmueble pertenece a un
 * proyecto (`prj_`) y a un subtipo (`typ_`), así que el listado joinea AMBOS
 * (`$lookup` a projects + typeproperties, el mismo molde relacional que estrenaron
 * agencias/sociedades/proyectos/subtipos, pero por partida doble).
 *
 * A diferencia de los subtipos —cuyo `code` es único por proyecto—, `name` y `code`
 * del inmueble son únicos GLOBAL. El inmueble tampoco lleva autoría: el schema Mongo
 * no tiene `createdBy`/`updatedBy`, así que el detalle no muestra «Creado por».
 *
 * IUSI (Money): `iusiAmount` es el monto del Impuesto Único Sobre Inmuebles en GTQ.
 * El DoD de la migración exige que el dinero viaje como `Money.amountMinor` en el
 * wire v2, NUNCA como float. OJO — la conversión vive SÓLO en el adapter/presenter
 * v2: en Mongo el campo sigue siendo un `number` plano (mayor, quetzales) para no
 * romper a los consumidores legacy (print/generación de documentos, negociaciones)
 * que lo leen crudo. El presenter hace `number → Money` y el service `Money → number`
 * al persistir. Ver `v2-admin-properties.service.ts`.
 */

/**
 * Referencia al proyecto dueño, wire-safe: el `_id` no cruza el adapter, así que se
 * publica su `prj_` junto al nombre y el código. Nullable por robustez del listado:
 * una fila cuyo proyecto perdió el `publicId` presenta `null` en vez de tumbar la
 * página entera; `project` es required en el dominio, así que en la práctica siempre
 * se resuelve.
 */
export const V2AdminPropertyProjectSchema = z
  .object({
    id: PublicIdSchemas.project,
    name: z.string(),
    code: z.string()
  })
  .nullable()

/**
 * Referencia al subtipo, wire-safe: `typ_` + código + nombre. El nombre del subtipo
 * puede ser `null` (un subtipo puede existir sólo por su código). Nullable por la
 * misma robustez que el proyecto.
 */
export const V2AdminPropertyTypeSchema = z
  .object({
    id: PublicIdSchemas.typeProperty,
    code: z.string(),
    name: z.string().nullable()
  })
  .nullable()

/**
 * IUSI en el wire v2: Money no negativo (GTQ) o `null` cuando el inmueble no lo tiene
 * cargado. El presenter convierte el `number` plano de Mongo con `moneyFromMajor`.
 */
export const V2AdminPropertyIusiSchema = NonNegativeMoneySchema.nullable()

/** Fila del listado y del detalle. */
export const V2AdminPropertySchema = z.object({
  id: PublicIdSchemas.property,
  project: V2AdminPropertyProjectSchema,
  typeProperty: V2AdminPropertyTypeSchema,
  /** Único GLOBAL (case-insensitive); se recorta al escribir. */
  name: z.string(),
  /** Único GLOBAL (case-insensitive); se recorta al escribir. */
  code: z.string(),
  // Estado de sistema (lo gobiernan negociaciones/reservas, no el admin): sólo sale.
  availabilityStatus: z.nativeEnum(AVAILABILITY_STATUS),
  inNegotiation: z.boolean(),
  active: z.boolean(),
  // Identificación física del inmueble. Las áreas son MEDIDAS (m²/v²), no dinero:
  // viajan como número plano. Sólo `iusiAmount` es Money.
  unitType: z.nativeEnum(UNIT_TYPE).nullable(),
  tipo: z.string().nullable(),
  structureType: z.nativeEnum(STRUCTURE_TYPE).nullable(),
  structureNumber: z.number().nullable(),
  identifier: z.number().nullable(),
  constructionM2: z.number().nullable(),
  terrainM2: z.number().nullable(),
  terrainV2: z.number().nullable(),
  terraceM2: z.number().nullable(),
  parkingM2: z.number().nullable(),
  property: z.string().nullable(),
  folio: z.string().nullable(),
  book: z.string().nullable(),
  waterMeter: z.string().nullable(),
  electricityMeter: z.string().nullable(),
  iusiAmount: V2AdminPropertyIusiSchema,
  telephone: z.string().nullable(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema
})

/** Página cursor-paginada del listado (ADR 0019). */
export const V2AdminPropertiesPageSchema = z.object({
  data: z.array(V2AdminPropertySchema),
  pagination: V2CursorPaginationSchema
})

/**
 * Total y facetas `availabilityStatus` (cinco claves, siempre presentes aunque estén
 * en cero) y `active`, con los mismos filtros allowlist del listado. Cada faceta
 * ignora el filtro de su propia dimensión; `total` respeta la consulta completa.
 * Proyecto y subtipo NO son facetas: se filtran por nombre/código (texto).
 */
export const V2AdminPropertiesSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  availabilityStatus: z.object({
    AVAILABLE: z.number().int().nonnegative(),
    RESERVED: z.number().int().nonnegative(),
    PENDING_AUTHORIZATION: z.number().int().nonnegative(),
    IN_NEGOTIATION: z.number().int().nonnegative(),
    SOLD: z.number().int().nonnegative()
  }),
  active: z.object({
    true: z.number().int().nonnegative(),
    false: z.number().int().nonnegative()
  })
})

/**
 * Entrada mínima del selector de inmuebles. `project` (`prj_`) permite a los
 * formularios cascadear: primero se elige el proyecto y el selector de inmuebles se
 * filtra por él en el cliente.
 */
export const V2AdminPropertyLookupItemSchema = z.object({
  id: PublicIdSchemas.property,
  code: z.string(),
  name: z.string(),
  project: PublicIdSchemas.project
})

export const V2AdminPropertyLookupSchema = z.object({
  data: z.array(V2AdminPropertyLookupItemSchema)
})

/**
 * Filtros del lookup de inmuebles. Los dos existen para el selector de NEGOCIACIONES.
 *
 * **`onlyAvailable`** deja fuera lo que no se puede vender: lo que ya está en otra negociación
 * y lo que tiene un estado de disponibilidad bloqueante. No es un filtro cosmético — sin él el
 * selector ofrece inmuebles que el guardado va a rechazar por el invariante.
 *
 * **`include`** es el escape para editar: al abrir una negociación, SU inmueble está
 * `inNegotiation: true` —por ella misma— así que el filtro lo excluiría y el select aparecería
 * vacío con un valor que no está en la lista. v1 resuelve lo mismo recibiendo la negociación y
 * yendo a buscarle el inmueble; acá lo manda quien ya lo tiene, y así el catálogo no necesita
 * saber que existen las negociaciones.
 */
export const V2AdminPropertyLookupQuerySchema = z.object({
  onlyAvailable: z.coerce.boolean().optional(),
  include: PublicIdSchemas.property.optional()
})

/**
 * Alta de inmueble. El proyecto y el subtipo entran por sus ids públicos (`prj_` /
 * `typ_`), no por ObjectId como el wire legacy; el service los resuelve y valida que
 * existan. `name` y `code` se recortan y deben ser únicos GLOBAL (case-insensitive).
 *
 * `availabilityStatus` e `inNegotiation` NO entran: son estado de sistema que
 * gobiernan negociaciones y reservas, no el catálogo. `iusiAmount` entra como Money
 * (GTQ) y el service lo baja a número plano al persistir.
 */
export const V2CreateAdminPropertySchema = z.object({
  project: PublicIdSchemas.project,
  typeProperty: PublicIdSchemas.typeProperty,
  name: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  code: requiredString()
    .transform((value) => value.trim())
    .pipe(requiredString()),
  unitType: z.nativeEnum(UNIT_TYPE).optional(),
  tipo: z.string().trim().optional(),
  structureType: z.nativeEnum(STRUCTURE_TYPE).optional(),
  structureNumber: z.number().min(1).optional(),
  identifier: z.number().optional(),
  constructionM2: z.number().nonnegative().optional(),
  terrainM2: z.number().nonnegative().optional(),
  terrainV2: z.number().nonnegative().optional(),
  terraceM2: z.number().nonnegative().optional(),
  parkingM2: z.number().nonnegative().optional(),
  property: z.string().trim().optional(),
  folio: z.string().trim().optional(),
  book: z.string().trim().optional(),
  waterMeter: z.string().trim().optional(),
  electricityMeter: z.string().trim().optional(),
  iusiAmount: NonNegativeMoneySchema.optional(),
  telephone: z.string().trim().optional(),
  active: z.boolean().optional()
})

/**
 * Edición parcial: un id desconocido responde 404 (nunca hace upsert). Renombrar a un
 * `name`/`code` existente re-chequea la unicidad GLOBAL excluyendo la propia fila.
 * Mover a otro proyecto/subtipo (`prj_`/`typ_`) valida que existan.
 */
export const V2UpdateAdminPropertySchema = V2CreateAdminPropertySchema.partial()

export type V2AdminPropertyProject = z.infer<typeof V2AdminPropertyProjectSchema>
export type V2AdminPropertyType = z.infer<typeof V2AdminPropertyTypeSchema>
export type V2AdminProperty = z.infer<typeof V2AdminPropertySchema>
export type V2AdminPropertiesPage = z.infer<typeof V2AdminPropertiesPageSchema>
export type V2AdminPropertiesSummary = z.infer<typeof V2AdminPropertiesSummarySchema>
export type V2AdminPropertyLookupItem = z.infer<typeof V2AdminPropertyLookupItemSchema>
export type V2AdminPropertyLookup = z.infer<typeof V2AdminPropertyLookupSchema>
export type V2AdminPropertyLookupQuery = z.infer<typeof V2AdminPropertyLookupQuerySchema>
export type V2CreateAdminPropertyInput = z.infer<typeof V2CreateAdminPropertySchema>
export type V2UpdateAdminPropertyInput = z.infer<typeof V2UpdateAdminPropertySchema>
